import React from 'react';
import { View, Text, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  selectWeekProgressSummary,
  WeekProgressSummary,
} from '../../redux/selectors/weekProgress';
import { Paprika, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import { isDesktopWeb } from '../../utils/platformDetection';
import { buildHeadline } from './weekProgressStripText';

interface WeekProgressStripProps {
  weekStartDate: string;
  /** Optional override for "today" — used only in tests. Production omits. */
  today?: string;
}

const PROTEIN_COLORS = {
  meat: '#b85a3a',
  fish: '#4a90a8',
  veggie: '#6ba84a',
} as const;

export const WeekProgressStrip: React.FC<WeekProgressStripProps> = ({
  weekStartDate,
  today,
}) => {
  const { t } = useTranslation();
  const summary: WeekProgressSummary = useSelector((state: any) =>
    selectWeekProgressSummary(state, weekStartDate, today),
  );

  const { width } = useWindowDimensions();
  const isDesktop = Platform.OS === 'web' && isDesktopWeb() && width >= 768;

  if (summary.total === 0) return null;

  const cookedPct = (summary.cooked / summary.total) * 100;
  const skippedPct = (summary.skipped / summary.total) * 100;

  return (
    <View
      style={[
        styles.container,
        isDesktop ? styles.containerDesktop : styles.containerMobile,
      ]}
    >
      <View style={isDesktop ? styles.headlineWrapDesktop : styles.headlineWrapMobile}>
        <Text style={styles.headline}>{buildHeadline(summary, t)}</Text>
        <View style={styles.bar}>
          <View style={[styles.barCooked, { width: `${cookedPct}%` }]} />
          <View style={[styles.barSkipped, { width: `${skippedPct}%` }]} />
        </View>
      </View>

      <View style={styles.tiles}>
        {(['meat', 'fish', 'veggie'] as const).map((kind) => (
          <ProteinTile
            key={kind}
            label={kind === 'meat' ? t('thisWeek.mealTags.meat') : kind === 'fish' ? t('thisWeek.mealTags.fish') : t('thisWeek.mealTags.veggie')}
            cooked={summary.byProtein[kind].cooked}
            planned={summary.byProtein[kind].planned}
            color={PROTEIN_COLORS[kind]}
          />
        ))}
      </View>
    </View>
  );
};

const ProteinTile: React.FC<{
  label: string;
  cooked: number;
  planned: number;
  color: string;
}> = ({ label, cooked, planned, color }) => (
  <View style={styles.tile}>
    <Text style={[styles.tileNumber, { color }]}>
      {cooked}
      <Text style={styles.tileDenom}>/{planned}</Text>
    </Text>
    <Text style={styles.tileLabel}>{label}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
    padding: PaprikaSpacing.md,
  },
  containerDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.lg,
    marginHorizontal: PaprikaSpacing.lg,
    marginVertical: PaprikaSpacing.md,
  },
  containerMobile: {
    marginHorizontal: PaprikaSpacing.md,
    marginVertical: PaprikaSpacing.sm,
  },
  headlineWrapDesktop: { flex: 1 },
  headlineWrapMobile: { marginBottom: PaprikaSpacing.sm },
  headline: {
    fontSize: 13,
    color: Paprika.textSecondary,
    fontWeight: '500',
  },
  bar: {
    height: 5,
    backgroundColor: '#f0ebe2',
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 6,
    flexDirection: 'row',
  },
  barCooked: { backgroundColor: '#27ae60', height: '100%' },
  barSkipped: { backgroundColor: '#c4b5a0', height: '100%' },
  tiles: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
  },
  tile: {
    flex: 1,
    minWidth: 56,
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 4,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 8,
  },
  tileNumber: {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 18,
  },
  tileDenom: {
    color: Paprika.textMuted,
    fontWeight: '400',
  },
  tileLabel: {
    fontSize: 9,
    letterSpacing: 1.2,
    color: Paprika.textMuted,
    textTransform: 'uppercase',
    marginTop: 2,
  },
});

export default WeekProgressStrip;
